import React, {useEffect, useMemo, useState} from 'react';
import cx from 'classnames';
import styles from './styles.module.scss';
import {CheckBox} from '@priority-software/priority-style-react';
import {InventoryInfo} from './InventoryInfo';
import {PartsDetails} from '../../types';
import {
    IMG_FIELD_NAME,
    INVENTORY_INFO_QUERY_KEY,
    PARTS_SEARCH_MONITOR,
    PARTS_SEARCH_MONITOR_CHANGEQUANTITY,
    PARTS_SEARCH_MONITOR_VIEWINV,
    PART_CHECKBOX_NAME,
    PART_INVENTORY_FIELDS
} from '../../consts';
import Details from './Details';
import imgPlaceholder from '../../resources/imgplaceholder.png';
import Title from './Title';
import Description from './Description';
import {observer} from 'mobx-react-lite';
import {partsStore} from '../../PartsStore';
import {fetchInventoryData} from '../../service';
import {useQuery, useIsFetching} from 'react-query';
import {serializeInventoryDetails} from '../../serialize';
import {getSelectedColumnsData} from '../../actions';
import QuantitySelector from './QuantitySelector';
import {sendMonitor} from '../../../../utils/monitoring';

interface Props {
    part: PartsDetails;
    isFocused: boolean;
    innerRef: any;
    onClick: (e) => void;
    onSelectItem: (item) => void;
    onFocusCheckBox: () => void;
    invButtonRef: any;
    hasTquant: boolean;
}

const PartItem = observer(
    ({
        part,
        isFocused,
        onClick,
        onSelectItem,
        innerRef,
        invButtonRef,
        onFocusCheckBox,
        hasTquant
    }: Props) => {
        const {selectedColumns, selectedItems, session, showQuantitySelector} = partsStore;
        const {partName, columns} = part;

        const [inventoryDetails, setInventoryDetails] = useState<any>();
        const [isDataShown, setIsDataShown] = useState(false);
        const [queryEnabled, setQueryEnabled] = useState(false);

        const isFetching = useIsFetching({queryKey: [INVENTORY_INFO_QUERY_KEY]});

        const selectedInventory = useMemo(
            () => selectedColumns.filter(col => PART_INVENTORY_FIELDS.includes(col)),
            [selectedColumns]
        );

        const handleSetData = data => {
            if (data?.records) {
                const inv = serializeInventoryDetails(
                    data?.records[0]?.Fields,
                    data.columns,
                    selectedInventory
                );
                setInventoryDetails(inv);
            } else {
                setInventoryDetails([{}]);
            }
        };

        const handleShowInv = async () => {
            sendMonitor(`${PARTS_SEARCH_MONITOR}${PARTS_SEARCH_MONITOR_VIEWINV}`);
            setIsDataShown(true);
        };

        const onCheckBoxClick = e => {
            e.stopPropagation();
            onSelectItem(part);
            if (showQuantitySelector && !isChecked && e.target && part.selectedQuant === 0) {
                onQuantChange(1);
            }
        };

        const onQuantChange = (quant: number) => {
            part.selectedQuant = quant;
            sendMonitor(`${PARTS_SEARCH_MONITOR}${PARTS_SEARCH_MONITOR_CHANGEQUANTITY}`);
        };

        useEffect(() => {
            if (data) {
                handleSetData(data);
            }
        }, [selectedInventory]);

        useEffect(() => {
            if (isFetching === 0 && isDataShown && !queryEnabled) {
                setQueryEnabled(true);
            }
        }, [isFetching, isDataShown]);

        const {data, isLoading} = useQuery(
            [INVENTORY_INFO_QUERY_KEY, session, part.keys],
            () => fetchInventoryData(session, part.keys),
            {
                enabled: queryEnabled,
                onSuccess(data) {
                    if (data) {
                        handleSetData(data);
                    }
                }
            }
        );

        const details = useMemo(
            () => getSelectedColumnsData(selectedColumns, columns),
            [selectedColumns]
        );

        const imgField = useMemo(
            () => details.find(col => col.columnName === IMG_FIELD_NAME),
            [details]
        );
        const isChecked = selectedItems.has(partName);

        const containerClasses = cx(styles.itemCard, {
            [styles.itemCardWithInventory]: !!selectedInventory?.length,
            [styles.focusItem]: isFocused,
            [styles.checkItem]: isChecked
        });

        const imgValue = !!imgField?.value ? (imgField.value as string) : imgPlaceholder;

        return (
            <div onClick={onClick} ref={innerRef} className={containerClasses}>
                <div className={styles.itemCheckboxImageContainer}>
                    <div onClick={onCheckBoxClick}>
                        <CheckBox
                            checked={isChecked}
                            name={PART_CHECKBOX_NAME}
                            onFocus={onFocusCheckBox}
                        />
                    </div>
                    {imgField && <img src={imgValue} className={styles.partItemImg} />}
                </div>
                <div className={styles.itemDetails}>
                    <Title part={part} />
                    {showQuantitySelector && hasTquant && (
                        <QuantitySelector
                            onCheckBoxChange={onCheckBoxClick}
                            isChecked={isChecked}
                            partQuant={part.selectedQuant}
                            onQuantChange={onQuantChange}
                            partUnitName={part.punitName}
                        />
                    )}
                    <Description part={part} />
                    <Details partName={partName} columnsData={details} />
                </div>
                {!!selectedInventory?.length && (
                    <div className={styles.inventory}>
                        <InventoryInfo
                            ref={invButtonRef}
                            onOpen={handleShowInv}
                            inventoryData={inventoryDetails}
                            isOpen={!!inventoryDetails?.length}
                            partName={partName}
                            isLoading={isLoading || (isDataShown && !inventoryDetails?.length)}
                        />
                    </div>
                )}
            </div>
        );
    }
);

export default PartItem;
